import React, { useEffect, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/components/ui/data-table';
import { DetailsPanel } from '@/components/ui/details-panel';
import type { EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import {
	ResizablePanelGroup,
	ResizablePanel,
	ResizableHandle
} from '@/components/ui/resizable';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useLeadingTrailingDebounce } from '@/hooks/use-leading-trailing-debounce';

interface ListPageLayoutProps<TData> {
	// DataTable props
	columns: ColDef<TData>[];
	data: TData[];
	title: string;
	description: string;
	searchPlaceholder?: string;
	showSearch?: boolean;
	onSearchValueChange?: (value: string) => void;
	clearSearchToken?: number;
	isLoading: boolean;
	onRowClick: (row: TData) => void;

	// Actions
	onCreate?: () => void;
	onRefresh: () => void;
	onClone?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;

	// Pagination props
	hasNextPage?: boolean;
	isLoadingMore?: boolean;
	onLoadMore?: () => void;
	/** Localized name of the entity type for "Viewing X entities" text */
	entityName?: string;

	// Details panel props
	/** @deprecated Panel is now always open on desktop. Kept for backwards compatibility with mobile. */
	detailsPanelOpen?: boolean;
	/** @deprecated Panel is now always open on desktop. Used only for mobile back button. */
	onCloseDetails?: () => void;
	selectedItem: TData | null;
	detailsTitle: string;
	onEdit?: (id: string) => void;
	onDelete?: (id: string) => void;
	renderDetailsContent: (item: TData) => React.ReactNode;
	getRecordId?: (data: TData) => string;
	getDetailsDescription?: (data: TData) => React.ReactNode;
	getStatusBadge?: (data: TData) => React.ReactNode;
	renderDetailsHeaderAction?: (data: TData) => React.ReactNode;
	// Enhanced header props
	renderEntityIcon?: (data: TData) => React.ReactNode;
	getEntityTypeName?: (data: TData) => string;
	getHeaderSubtitle?: (data: TData) => React.ReactNode;
	getLastUpdated?: (data: TData) => string;
	renderDetailsHeader?: (data: TData, actions: EntityDetailsHeaderActions) => React.ReactNode;

	// Optional customization
	searchKey?: string;
	showCreateButton?: boolean;
	showRefreshButton?: boolean;
	autoSelectFirst?: boolean;
	renderCustomFilters?: () => React.ReactNode;
	/** Debounce delay for row selection in ms (default: 150ms). Set to 0 to disable. */
	selectionDebounceMs?: number;
	/** Filename for data export (without extension). Defaults to title. */
	exportFilename?: string;
	/** Stable unique identifier for the DataGrid instance. Used as the localStorage key
	 *  for persisting column state (widths, order, pinning) across sessions.
	 *  Must be a constant kebab-case string, e.g. 'contracts-list'. */
	gridId: string;
}

export function ListPageLayout<TData = any>({
	columns,
	data,
	title,
	description,
	searchPlaceholder = 'Search...',
	showSearch = true,
	onSearchValueChange,
	clearSearchToken,
	isLoading,
	onRowClick,
	onCreate,
	onRefresh,
	onClone,
	onOpenInNewWindow,
	hasNextPage = false,
	isLoadingMore = false,
	onLoadMore,
	entityName,
	detailsPanelOpen: _detailsPanelOpen, // Kept for backwards compatibility
	onCloseDetails,
	selectedItem,
	detailsTitle,
	onEdit,
	onDelete,
	renderDetailsContent,
	getRecordId,
	getDetailsDescription,
	getStatusBadge,
	renderDetailsHeaderAction,
	renderEntityIcon,
	getEntityTypeName,
	getHeaderSubtitle,
	getLastUpdated,
	renderDetailsHeader,
	showCreateButton = true,
	showRefreshButton = true,
	autoSelectFirst = true,
	renderCustomFilters,
	selectionDebounceMs = 200,
	exportFilename,
	gridId
}: ListPageLayoutProps<TData>) {
	const { t } = useTranslation('common');
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const [showMobileDetails, setShowMobileDetails] = useState(false);

	// Debounce row clicks to prevent excessive API calls during rapid keyboard navigation
	// Uses leading+trailing edge: first click fires immediately, subsequent rapid clicks are debounced
	const debouncedRowClick = useLeadingTrailingDebounce(
		onRowClick,
		selectionDebounceMs
	);

	// Use direct callback if debounce is disabled
	const handleRowClick =
		selectionDebounceMs > 0 ? debouncedRowClick : onRowClick;

	useEffect(() => {
		setPageHeader({
			title,
			description
		});

		return () => {
			resetPageHeader();
		};
	}, [title, description, setPageHeader, resetPageHeader]);

	// Show details on mobile when an item is selected
	useEffect(() => {
		if (selectedItem) {
			setShowMobileDetails(true);
		}
	}, [selectedItem]);

	const handleMobileBack = () => {
		setShowMobileDetails(false);
		if (onCloseDetails) {
			onCloseDetails();
		}
	};

	const resolveRecordId = React.useCallback(
		(row: TData): string => {
			if (!row) {
				return '';
			}

			if (getRecordId) {
				return getRecordId(row) ?? '';
			}

			if (typeof row === 'object' && row !== null) {
				const entries = Object.entries(row as Record<string, unknown>);
				for (const [key, value] of entries) {
					if (
						typeof value === 'string' &&
						value &&
						key.toLowerCase().endsWith('id')
					) {
						return value;
					}
				}
			}

			return '';
		},
		[getRecordId]
	);

	const handleRowDoubleClick = React.useCallback(
		(row: TData) => {
			const id = resolveRecordId(row);
			if (!id || !onEdit) {
				return;
			}

			onEdit(id);
		},
		[resolveRecordId, onEdit]
	);

	// Handle delete from context menu - bridge between row-based and ID-based delete
	const handleDeleteRow = React.useCallback(
		(row: TData) => {
			const id = resolveRecordId(row);
			if (id && onDelete) {
				onDelete(id);
			}
		},
		[resolveRecordId, onDelete]
	);

	// Handle clone from context menu - bridge between row-based and ID-based clone
	const handleCloneRow = React.useCallback(
		(row: TData) => {
			const id = resolveRecordId(row);
			if (id && onClone) {
				onClone(id);
			}
		},
		[resolveRecordId, onClone]
	);

	// Handle open in new window from context menu - bridge between row-based and ID-based
	const handleOpenInNewWindowRow = React.useCallback(
		(row: TData) => {
			const id = resolveRecordId(row);
			if (id && onOpenInNewWindow) {
				onOpenInNewWindow(id);
			}
		},
		[resolveRecordId, onOpenInNewWindow]
	);

	return (
		<div className="flex h-full overflow-hidden">
			{/* Mobile: Show either list or details */}
			<div
				className={`md:hidden flex-col w-full ${showMobileDetails && selectedItem ? 'hidden' : 'flex'}`}
			>
				<div className="flex flex-col flex-1 p-2 overflow-hidden">
					<div className="flex-1 overflow-hidden">
						<DataTable
							columns={columns}
							data={data}
							title={title}
							description={description}
							showCreateButton={showCreateButton}
							showRefreshButton={showRefreshButton}
							onCreate={onCreate}
							onRefresh={onRefresh}
							searchPlaceholder={searchPlaceholder}
							showSearch={showSearch}
							onSearchValueChange={onSearchValueChange}
							clearSearchToken={clearSearchToken}
							isLoading={isLoading}
							onRowClick={onRowClick}
							onRowDoubleClick={
								onEdit ? handleRowDoubleClick : undefined
							}
							onDelete={onDelete ? handleDeleteRow : undefined}
							onClone={onClone ? handleCloneRow : undefined}
							onOpenInNewWindow={onOpenInNewWindow ? handleOpenInNewWindowRow : undefined}
							autoSelectFirst={autoSelectFirst}
							renderCustomFilters={renderCustomFilters}
							selectedRowId={
								selectedItem
									? resolveRecordId(selectedItem)
									: null
							}
							getRowId={resolveRecordId}
							exportFilename={exportFilename || title}
							gridId={gridId}
						/>
					</div>
					{/* Pagination Bar - Mobile */}
					{(onLoadMore || entityName) && (
						<div className="flex items-center justify-end gap-4 px-2 pt-1 border-t border-gray-200 dark:border-slate-700">
							{entityName && (
								<span className="text-sm text-muted-foreground">
									{t('pagination.viewingCount', {
										count: data.length,
										entityName
									})}
								</span>
							)}
							{hasNextPage && onLoadMore && (
								<button
									type="button"
									onClick={onLoadMore}
									disabled={isLoadingMore}
									className="text-sm text-primary hover:text-primary/80 hover:underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
								>
									{isLoadingMore && (
										<Loader2 className="w-3 h-3 animate-spin" />
									)}
									({t('pagination.loadMore')})
								</button>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Mobile: Details panel with back button */}
			<div
				className={`md:hidden flex-col w-full ${showMobileDetails && selectedItem ? 'flex' : 'hidden'}`}
			>
				<div className="flex items-center gap-2 p-2 bg-white border-b border-gray-200 dark:bg-slate-900 dark:border-slate-700">
					<Button
						variant="ghost"
						size="sm"
						onClick={handleMobileBack}
						className="flex items-center gap-2"
					>
						<ArrowLeft className="w-4 h-4" />
						{t('buttons.backToList')}
					</Button>
				</div>
				<div className="flex-1 overflow-hidden">
					<DetailsPanel
						data={selectedItem}
						title={detailsTitle}
						onEdit={onEdit}
						onDelete={onDelete}
						onClone={onClone}
						onOpenInNewWindow={onOpenInNewWindow}
						renderContent={renderDetailsContent}
						getRecordId={getRecordId}
						getDescription={getDetailsDescription}
						getStatusBadge={getStatusBadge}
						renderHeaderAction={renderDetailsHeaderAction}
						renderEntityIcon={renderEntityIcon}
						getEntityTypeName={getEntityTypeName}
						getHeaderSubtitle={getHeaderSubtitle}
						getLastUpdated={getLastUpdated}
						renderHeader={renderDetailsHeader}
						alwaysOpen={true}
						showCloseButton={false}
					/>
				</div>
			</div>

			{/* Desktop: Resizable panels */}
			<ResizablePanelGroup
				direction="horizontal"
				className="!hidden md:!flex h-full w-full"
			>
				<ResizablePanel defaultSize={60} minSize={30}>
					<div className="flex flex-col h-full">
						<div className="flex flex-col flex-1 p-4 pt-6 overflow-hidden">
							<div className="flex-1 overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 shadow-[0_2px_8px_0px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_8px_0px_rgba(0,0,0,0.25)] bg-white dark:bg-slate-900 flex flex-col">
								<DataTable
									columns={columns}
									data={data}
									title={title}
									description={description}
									showCreateButton={showCreateButton}
									showRefreshButton={showRefreshButton}
									onCreate={onCreate}
									onRefresh={onRefresh}
									searchPlaceholder={searchPlaceholder}
									showSearch={showSearch}
									onSearchValueChange={onSearchValueChange}
									clearSearchToken={clearSearchToken}
									isLoading={isLoading}
									onRowClick={handleRowClick}
									onRowDoubleClick={
										onEdit
											? handleRowDoubleClick
											: undefined
									}
									onDelete={
										onDelete ? handleDeleteRow : undefined
									}
									onClone={
										onClone ? handleCloneRow : undefined
									}
									onOpenInNewWindow={
										onOpenInNewWindow ? handleOpenInNewWindowRow : undefined
									}
									autoSelectFirst={autoSelectFirst}
									renderCustomFilters={renderCustomFilters}
									selectedRowId={
										selectedItem
											? resolveRecordId(selectedItem)
											: null
									}
									getRowId={resolveRecordId}
									exportFilename={exportFilename || title}
									gridId={gridId}
								/>
								{/* Pagination Bar - Desktop */}
								{(onLoadMore || entityName) && (
									<div className="flex items-center justify-end gap-4 px-4 py-2 border-t border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50">
										{entityName && (
											<span className="text-sm text-muted-foreground">
												{t('pagination.viewingCount', {
													count: data.length,
													entityName
												})}
											</span>
										)}
										{hasNextPage && onLoadMore && (
											<button
												type="button"
												onClick={onLoadMore}
												disabled={isLoadingMore}
												className="text-sm text-primary hover:text-primary/80 hover:underline disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
											>
												{isLoadingMore && (
													<Loader2 className="w-3 h-3 animate-spin" />
												)}
												({t('pagination.loadMore')})
											</button>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</ResizablePanel>{' '}
				<ResizableHandle />
				<ResizablePanel defaultSize={40} minSize={25} maxSize={50} className="shadow-[-2px_0_8px_0px_rgba(0,0,0,0.08)] dark:shadow-[-2px_0_8px_0px_rgba(0,0,0,0.25)]">
					<DetailsPanel
						data={selectedItem}
						title={detailsTitle}
						onEdit={onEdit}
						onDelete={onDelete}
						onClone={onClone}
						onOpenInNewWindow={onOpenInNewWindow}
						renderContent={renderDetailsContent}
						getRecordId={getRecordId}
						getDescription={getDetailsDescription}
						getStatusBadge={getStatusBadge}
						renderHeaderAction={renderDetailsHeaderAction}
						renderEntityIcon={renderEntityIcon}
						getEntityTypeName={getEntityTypeName}
						getHeaderSubtitle={getHeaderSubtitle}
						getLastUpdated={getLastUpdated}
						renderHeader={renderDetailsHeader}
						alwaysOpen={true}
						showCloseButton={false}
					/>
				</ResizablePanel>
			</ResizablePanelGroup>
		</div>
	);
}
